// ─────────────────────────────────────────────────────────────────────────────
// AttendTrack — API Client
// Single set of storage keys — one active session at a time
// Portal type (employee/hr) tracked separately in at_portal key
// ─────────────────────────────────────────────────────────────────────────────

const BASE = "/api";

export const TokenStore = {
  // Token & session
  save:         (token)  => localStorage.setItem("at_token",  token),
  get:          ()       => localStorage.getItem("at_token"),
  clear:        ()       => localStorage.removeItem("at_token"),

  // Plan
  savePlan:     (plan)   => localStorage.setItem("at_plan",   JSON.stringify(plan)),
  getPlan:      ()       => { try { return JSON.parse(localStorage.getItem("at_plan")); } catch { return null; } },

  // User info
  saveUser:     (user)   => localStorage.setItem("at_user",   JSON.stringify(user)),
  getUser:      ()       => { try { return JSON.parse(localStorage.getItem("at_user")); } catch { return null; } },

  // Which portal is active: "employee" | "hr"
  savePortal:   (p)      => localStorage.setItem("at_portal", p),
  getPortal:    ()       => localStorage.getItem("at_portal") || null,

  // Tenant ID — always remembered across sessions
  saveTenantId: (id)     => localStorage.setItem("at_tenant", id),
  getTenantId:  ()       => localStorage.getItem("at_tenant") || "",

  // Clear session but keep tenant ID
  clearSession: () => {
    ["at_token","at_plan","at_user","at_portal"].forEach(k => localStorage.removeItem(k));
  },
};

// ── Base fetch ────────────────────────────────────────────────────────────────
async function apiFetch(path, options = {}) {
  const token    = TokenStore.get();
  const tenantId = TokenStore.getTenantId();

  const headers = {
    "Content-Type": "application/json",
    ...(token    ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantId ? { "X-Tenant-ID":  tenantId }         : {}),
    ...options.headers,
  };

  const res  = await fetch(`${BASE}${path}`, { ...options, headers });

  // 401 — clear session, let app handle redirect to login
  if (res.status === 401) {
    TokenStore.clearSession();
    window.dispatchEvent(new Event("at_session_expired"));
    return null;
  }

  const data = await res.json();
  if (!res.ok) throw data;
  return data;
}

// ── Auth ──────────────────────────────────────────────────────────────────────
export const api = {

  login: async (email, password) => {
    const data = await apiFetch("/auth/login", {
      method: "POST",
      body: JSON.stringify({
        email,
        password,
        device_id:   "web-portal",
        device_name: "Web Portal",
      }),
    });
    if (!data) { const e = new Error("Login failed"); e.detail = "Login failed"; throw e; }
    TokenStore.save(data.token);
    TokenStore.savePlan(data.plan);
    // Save tenant_id from JWT
    try {
      const payload = JSON.parse(atob(data.token.split(".")[1]));
      if (payload.tenant_id) TokenStore.saveTenantId(payload.tenant_id);
    } catch(e) {}
    return data;
  },

  logout: async () => {
    try { await apiFetch("/auth/logout", { method: "POST" }); } catch(e) {}
    TokenStore.clearSession();
  },

  // ── Attendance ──────────────────────────────────────────────────────────────
  verifyLocation: (lat, lng) =>
    apiFetch("/attendance/verify-location", {
      method: "POST", body: JSON.stringify({ lat, lng }),
    }),

  checkIn:  (lat = 0, lng = 0) =>
    apiFetch("/attendance/checkin",  { method: "POST", body: JSON.stringify({ lat, lng }) }),

  checkOut: (lat = 0, lng = 0) =>
    apiFetch("/attendance/checkout", { method: "POST", body: JSON.stringify({ lat, lng }) }),

  getTodayAttendance:   () => apiFetch("/attendance/today"),

  getMe: () => apiFetch("/auth/me"),

  markWeekOff:       () =>
    apiFetch("/attendance/week-off", { method: "POST", body: JSON.stringify({}) }),

  getWeekOffStatus:  () =>
    apiFetch("/attendance/week-off/status"),
  getAttendanceHistory: (from_date, to_date) =>
    apiFetch(`/attendance/history?from_date=${from_date}&to_date=${to_date}`),

  // ── Leaves ──────────────────────────────────────────────────────────────────
  getMyLeaveApplications: () => apiFetch("/leaves/my-applications"),
  getMyLeaveBalance:      () => apiFetch("/leaves/my-balance"),
  getLeaveTypesForEmployee: () => apiFetch("/leaves/types"),

  applyLeave: (data) =>
    apiFetch("/leaves/apply", { method: "POST", body: JSON.stringify(data) }),

  getPendingLeaves: () => apiFetch("/leaves/pending"),

  approveLeave: (id) =>
    apiFetch(`/leaves/${id}/approve`, { method: "PATCH", body: JSON.stringify({}) }),

  rejectLeave: (id, rejection_note = "") =>
    apiFetch(`/leaves/${id}/reject`, { method: "PATCH", body: JSON.stringify({ rejection_note }) }),

  distributeFlexibleLeave: (applicationId, data) =>
    apiFetch(`/leaves/${applicationId}/distribute`, { method: "POST", body: JSON.stringify(data) }),

  // ── HR ────────────────────────────────────────────────────────────────────
  getOffices:   () => apiFetch("/hr/offices"),
  getEmployees: () => apiFetch("/hr/employees"),
  getLeaveTypes:() => apiFetch("/hr/leave-types"),

  addOffice: (data) =>
    apiFetch("/hr/offices", { method: "POST", body: JSON.stringify(data) }),

  addEmployee: (data) =>
    apiFetch("/hr/employees", { method: "POST", body: JSON.stringify(data) }),

  updateEmployee: (id, data) =>
    apiFetch(`/hr/employees/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  deactivateEmployee: (id) =>
    apiFetch(`/hr/employees/${id}`, { method: "DELETE" }),

  resetDevice: (id) =>
    apiFetch(`/hr/employees/${id}/device`, { method: "DELETE" }),

  assignOffices: (manager_id, office_ids) =>
    apiFetch("/hr/assignments", { method: "POST", body: JSON.stringify({ manager_id, office_ids }) }),

  getManagerAssignments: (manager_id) =>
    apiFetch(`/hr/assignments/${manager_id}`),

  getOfficeTodayAttendance: (office_id) =>
    apiFetch(`/hr/offices/${office_id}/today`),

  proxyCheckIn: (employee_id, reason) =>
    apiFetch("/attendance/proxy-checkin", {
      method: "POST",
      body: JSON.stringify({ employee_id, reason: reason || "Proxy by manager" }),
    }),

  proxyCheckOut: (employee_id, reason) =>
    apiFetch("/attendance/proxy-checkout", {
      method: "POST",
      body: JSON.stringify({ employee_id, reason: reason || "Proxy by manager" }),
    }),

  proxyWeekOff: (employee_id, reason) =>
    apiFetch("/attendance/proxy-week-off", {
      method: "POST",
      body: JSON.stringify({ employee_id, reason: reason || "Proxy week off by manager" }),
    }),

  overrideAttendanceStatus: (employee_id, new_status, note, leave_type_id = null) =>
    apiFetch("/attendance/override-status", {
      method: "POST",
      body: JSON.stringify({ employee_id, new_status, note, leave_type_id }),
    }),

  getOverrideConfig: () => apiFetch("/attendance/override-config"),

  getEmployeeLeaveBalance: (emp_id) =>
    apiFetch(`/hr/employees/${emp_id}/leave-balance`),

  getAttendanceReport: (params) => {
    const q = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null))
    ).toString();
    return apiFetch(`/hr/reports/attendance?${q}`);
  },
};

// ── Developer API ─────────────────────────────────────────────────────────────
// Uses sessionStorage (clears when tab closes) and a separate token key.
// Completely isolated from the normal employee/HR session.

const DEV_TOKEN_KEY = "at_dev_token";

function devFetch(path, options = {}) {
  const token     = sessionStorage.getItem(DEV_TOKEN_KEY);
  const isForm    = options.body instanceof FormData;
  const headers   = {
    ...(!isForm ? { "Content-Type": "application/json" } : {}),
    ...(token   ? { Authorization: `Bearer ${token}` }  : {}),
    ...options.headers,
  };
  return fetch(`${BASE}/dev${path}`, { ...options, headers }).then(async (res) => {
    if (res.status === 401) {
      sessionStorage.removeItem(DEV_TOKEN_KEY);
      return null;
    }
    // Guard: server may return plain-text on unhandled 500s
    let data;
    try {
      data = await res.json();
    } catch {
      const text = await res.text().catch(() => "");
      const e = new Error(text ? `Server error: ${text.slice(0, 120)}` : `HTTP ${res.status}`);
      e.status = res.status;
      throw e;
    }
    if (!res.ok) { const e = new Error(data.detail || "Request failed"); e.status = res.status; e.data = data; throw e; }
    return data;
  });
}

export const devApi = {
  login: async (username, password) => {
    const res  = await fetch(`${BASE}/dev/login`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ username, password }),
    });
    const data = await res.json();
    if (!res.ok) { const e = new Error(data.detail || "Login failed"); e.status = res.status; throw e; }
    sessionStorage.setItem(DEV_TOKEN_KEY, data.token);
    return data;
  },

  logout: () => sessionStorage.removeItem(DEV_TOKEN_KEY),

  isLoggedIn: () => {
    const token = sessionStorage.getItem(DEV_TOKEN_KEY);
    if (!token) return false;
    try {
      const { exp } = JSON.parse(atob(token.split(".")[1]));
      return exp * 1000 > Date.now();
    } catch { return false; }
  },

  // ── Tenants ───────────────────────────────────────────────────────────────
  getTenants: () => devFetch("/tenants"),

  createTenant: (data) =>
    devFetch("/tenants", { method: "POST", body: JSON.stringify(data) }),

  toggleTenantStatus: (tenantId) =>
    devFetch(`/tenants/${tenantId}/status`, { method: "PATCH" }),

  // ── Leave types ───────────────────────────────────────────────────────────
  getLeaveTypes: (tenantId) => devFetch(`/leave-types/${tenantId}`),

  addLeaveType: (tenantId, data) =>
    devFetch(`/leave-types/${tenantId}`, { method: "POST", body: JSON.stringify(data) }),

  deleteLeaveType: (tenantId, leaveTypeId) =>
    devFetch(`/leave-types/${tenantId}/${leaveTypeId}`, { method: "DELETE" }),

  // ── Bulk import ───────────────────────────────────────────────────────────
  importEmployees: (tenantId, file) => {
    const form = new FormData();
    form.append("file", file);
    return devFetch(`/import/employees/${tenantId}`, { method: "POST", body: form });
  },

  // ── Audit log ─────────────────────────────────────────────────────────────
  getAuditLog: (limit = 50) => devFetch(`/audit-log?limit=${limit}`),

  // ── Impersonation ─────────────────────────────────────────────────────────
  impersonate: async (tenantId) => {
    const data = await devFetch(`/impersonate/${tenantId}`, { method: "POST" });
    if (!data) throw new Error("Dev session expired.");
    // Store as a normal HR session so the main app works
    localStorage.setItem("at_token",  data.token);
    localStorage.setItem("at_portal", "hr");
    localStorage.setItem("at_plan",   JSON.stringify(data.plan));
    // Mark impersonation in sessionStorage so App.js can show the banner
    sessionStorage.setItem("at_dev_impersonating", JSON.stringify({
      tenant_id:   data.tenant_id,
      tenant_name: data.tenant_name,
      expires_in:  data.expires_in_minutes,
      started_at:  Date.now(),
    }));
    return data;
  },

  // ── Offices ───────────────────────────────────────────────────────────────
  getOffices: (tenantId) =>
    devFetch(`/offices/${tenantId}`),

  createOffice: (tenantId, data) =>
    devFetch(`/offices/${tenantId}`, { method: "POST", body: JSON.stringify(data) }),

  updateOffice: (tenantId, officeId, data) =>
    devFetch(`/offices/${tenantId}/${officeId}`, { method: "PATCH", body: JSON.stringify(data) }),

  deleteOffice: (tenantId, officeId) =>
    devFetch(`/offices/${tenantId}/${officeId}`, { method: "DELETE" }),
};
